import { 
  Box, 
  TextField, 
  Button, 
  IconButton, 
  Menu, 
  MenuItem, 
  Chip,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { 
  Search as SearchIcon, 
  FilterList as FilterIcon, 
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useState } from 'react';

interface FilterOption {
  key: string;
  label: string;
  value: string;
}

interface SectionToolbarProps {
  title?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onRefresh?: () => void;
  onExport?: () => void;
  onAdd?: () => void;
  filters?: FilterOption[];
  activeFilters?: string[];
  onFilterChange?: (filters: string[]) => void;
  loading?: boolean;
}

export function SectionToolbar({
  title,
  searchValue = '',
  onSearchChange,
  onRefresh,
  onExport,
  onAdd,
  filters = [],
  activeFilters = [],
  onFilterChange,
  loading = false
}: SectionToolbarProps) {
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleFilterSelect = (filterKey: string) => {
    if (!onFilterChange) return;
    
    const newFilters = activeFilters.includes(filterKey)
      ? activeFilters.filter(f => f !== filterKey)
      : [...activeFilters, filterKey];
    
    onFilterChange(newFilters);
  };

  const removeFilter = (filterKey: string) => {
    if (!onFilterChange) return;
    onFilterChange(activeFilters.filter(f => f !== filterKey));
  };

  return (
    <Box sx={{ mb: 3 }}>
      {title && (
        <Box sx={{ mb: 2 }}>
          <h4 style={{ margin: 0, fontWeight: 600 }}>{title}</h4>
        </Box>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* Search */}
        {onSearchChange && (
          <TextField
            size="small"
            placeholder="Cauta..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
        )}

        {/* Filters */}
        {filters.length > 0 && (
          <>
            <Button
              size="small"
              startIcon={<FilterIcon />}
              onClick={handleFilterClick}
              variant={activeFilters.length > 0 ? 'contained' : 'outlined'}
            >
              Filtre {activeFilters.length > 0 && `(${activeFilters.length})`}
            </Button>
            
            <Menu
              anchorEl={filterAnchor}
              open={Boolean(filterAnchor)}
              onClose={handleFilterClose}
            >
              {filters.map((filter) => (
                <MenuItem
                  key={filter.key}
                  onClick={() => handleFilterSelect(filter.key)}
                  selected={activeFilters.includes(filter.key)}
                >
                  {filter.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onRefresh && (
            <Tooltip title="Reimprospateaza">
              <span>
                <IconButton 
                  size="small" 
                  onClick={onRefresh}
                  disabled={loading}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          
          {onExport && (
            <Tooltip title="Exporta">
              <IconButton size="small" onClick={onExport}>
                <ExportIcon />
              </IconButton>
            </Tooltip>
          )}
          
          {onAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAdd}
              size="small"
            >
              Adauga
            </Button>
          )}
        </Box>
      </Box>

      {/* Active Filters */}
      {activeFilters.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {activeFilters.map((filterKey) => {
            const filter = filters.find(f => f.key === filterKey);
            return (
              <Chip
                key={filterKey}
                label={filter?.label || filterKey}
                size="small"
                onDelete={() => removeFilter(filterKey)}
                color="primary"
                variant="outlined"
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
